import { getErrorMessage } from '@/lib/functions';
import { formatGroupedAmount, parseGroupedAmount } from '@/lib/amountFormat';
import { useTranslation } from 'react-i18next';
﻿import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ActivityIndicator,
  KeyboardAvoidingView,
  Modal,
  Platform,
  Pressable,
  StyleSheet,
  Text,
  TextInput,
  View,
} from 'react-native';

import { Button } from '@/components/ui/Button';
import { CurrencyHint } from '@/components/CurrencyHint';
import { fontSize, radius, shadow, spacing } from '@/constants/theme';
import type { AppColors } from '@/constants/theme';
import { useTheme } from '@/contexts/ThemeContext';
import type { Agreement, AdvancePayment } from '@/lib/database.types';
import {
  acceptedAdvanceTotal,
  listAdvancePayments,
  proposeAdvancePayment,
  respondAdvancePayment,
} from '@/lib/advancePayments';
import { formatAmount, formatDateTime, genderedArticle } from '@/lib/format';
import { ErrorText } from '@/components/ErrorText';

type Props = {
  agreement: Agreement;
  userId: string;
  otherName: string;
  onDeclarePayment?: () => void;
  /** Καλείται όταν αποδεχτεί ο χρήστης μια προτεινόμενη προκαταβολή/δόση — δείχνει το ίδιο animation με τη Δήλωση Εξόφλησης. */
  onAccepted?: () => void;
  /** Ειδοποιεί τον γονέα αν εκκρεμεί δική μου απάντηση (Ναι/Όχι) σε πρόταση
   * προκαταβολής — χρησιμοποιείται για το auto-scroll στο σημείο εκκρεμούς
   * απόφασης όταν ανοίγει η οθόνη συμφωνίας. */
  onPendingChange?: (hasPending: boolean) => void;
};

/** Πεδίο «Προκαταβολή» μέσα σε ενεργή συμφωνία με χρηματικό ποσό (ευρώ). */
export function AdvancePaymentSection({ agreement, userId, otherName, onDeclarePayment, onAccepted, onPendingChange }: Props) {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const styles = useMemo(() => makeStyles(colors), [colors]);

  const [items, setItems] = useState<AdvancePayment[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [modal, setModal] = useState(false);
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    try {
      const rows = await listAdvancePayments(agreement.id);
      setItems(rows);
      setError(null);
    } catch (e) {
      // Σε αποτυχία διατηρούμε το προηγούμενο items (όχι μηδενισμό σε [])
      // — αλλιώς ένα παροδικό network/RLS σφάλμα εμφανίζεται σαν "καμία
      // προκαταβολή ακόμα", κρύβοντας ήδη αποδεκτές δόσεις από το υπόλοιπο.
      setError(getErrorMessage(e, t('advancePayment.errorLoadFailed')));
    } finally {
      setLoading(false);
    }
  }, [agreement.id, t]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const total = Number(agreement.amount ?? 0);
  const acceptedTotal = acceptedAdvanceTotal(items);
  // Η πλήρης δήλωση/επιβεβαίωση εξόφλησης δεν περνάει από τον πίνακα
  // advance_payments — μηδενίζει το υπόλοιπο ανεξάρτητα από τυχόν δόσεις.
  const fullyPaid = !!agreement.payment_confirmed_at;
  const remaining = fullyPaid ? 0 : Math.max(0, total - acceptedTotal);
  const pending = items.find((i) => i.status === 'pending');
  const lastRejectedMine = items.find(
    (i) => i.status === 'rejected' && i.proposer_id === userId,
  );

  useEffect(() => {
    onPendingChange?.(!!pending && pending.proposer_id !== userId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pending?.id, pending?.proposer_id, userId]);

  async function submit() {
    setError(null);
    const value = parseGroupedAmount(amount) ?? NaN;
    if (!Number.isFinite(value) || value <= 0) {
      setError(t('errors.invalidAmount'));
      return;
    }
    if (value > remaining) {
      setError(t('advancePayment.amountExceedsRemaining', { amount: formatAmount(remaining) }));
      return;
    }
    setBusy(true);
    try {
      await proposeAdvancePayment(agreement.id, userId, value, note);
      setModal(false);
      setAmount('');
      setNote('');
      await reload();
    } catch (e) {
      setError(getErrorMessage(e, t('advancePayment.errorRecordFailed')));
    } finally {
      setBusy(false);
    }
  }

  async function respond(accept: boolean) {
    if (!pending) return;
    setBusy(true);
    try {
      await respondAdvancePayment(pending.id, accept);
      await reload();
      if (accept) onAccepted?.();
    } catch (e) {
      setError(getErrorMessage(e, t('advancePayment.errorActionFailed')));
    } finally {
      setBusy(false);
    }
  }

  if (loading) {
    return (
      <View style={styles.card}>
        <ActivityIndicator color={colors.primary} />
      </View>
    );
  }

  const iAmProposer = pending?.proposer_id === userId;

  return (
    <View style={styles.card}>
      <Text style={styles.sectionTitle}>{t('advancePayment.sectionTitle')}</Text>

      <Row label={t('advancePayment.totalAmount')} value={formatAmount(total, agreement.currency)} styles={styles} />
      <Row label={t('advancePayment.advance')} value={formatAmount(fullyPaid ? total : acceptedTotal, agreement.currency)} accent styles={styles} />
      <Row label={t('advancePayment.remaining')} value={formatAmount(remaining, agreement.currency)} strong styles={styles} />
      <View style={{ alignItems: 'flex-end', marginTop: -4 }}>
        <CurrencyHint amount={remaining} currency={agreement.currency} />
      </View>
      {fullyPaid && <Text style={styles.paidNotice}>{t('advancePayment.fullyPaid')}</Text>}

      {/* Εκκρεμής πρόταση προκαταβολής */}
      {pending && (
        <View style={[styles.notice, iAmProposer && styles.noticeWaiting]}>
          {iAmProposer ? (
            <Text style={styles.noticeText}>
              {t('advancePayment.pendingMine', { amount: formatAmount(Number(pending.amount), agreement.currency), name: otherName })}
            </Text>
          ) : (
            <>
              <Text style={styles.noticeText}>
                {t('advancePayment.pendingOther', {
                  article: genderedArticle(otherName),
                  name: otherName,
                  amount: formatAmount(Number(pending.amount), agreement.currency),
                  remaining: formatAmount(Math.max(0, total - acceptedTotal - Number(pending.amount)), agreement.currency),
                })}
              </Text>
              {!!pending.note?.trim() && <Text style={styles.noteText}>«{pending.note}»</Text>}
              <View style={styles.respondRow}>
                <View style={styles.respondBtn}>
                  <Button title={t('advancePayment.confirmYes')} icon="checkmark" loading={busy} onPress={() => respond(true)} />
                </View>
                <View style={styles.respondBtn}>
                  <Button
                    title={t('advancePayment.confirmNo')}
                    icon="close"
                    variant="secondary"
                    danger
                    loading={busy}
                    onPress={() => respond(false)}
                  />
                </View>
              </View>
            </>
          )}
        </View>
      )}

      {/* Ειδοποίηση απόρριψης προς τον προτείνοντα */}
      {!pending && lastRejectedMine && (
        <Text style={styles.rejected}>
          {t('advancePayment.rejectedNotice', { amount: formatAmount(Number(lastRejectedMine.amount), agreement.currency) })}
        </Text>
      )}

      {/* Καταγραφή νέας προκαταβολής + Δήλωση Εξόφλησης — το ένα κάτω από το
          άλλο, πλήρες πλάτος (πριν χωρούσαν δίπλα-δίπλα και το κείμενο έκοβε
          άσχημα). Και τα δύο κρύβονται όσο εκκρεμεί δήλωση εξόφλησης προς
          επιβεβαίωση. */}
      {!pending && !agreement.payment_declared_by && (
        remaining > 0 ? (
          <View style={styles.btnRow}>
            <Button
              title={t('advancePayment.action_add')}
              icon="cash-outline"
              variant="secondary"
              onPress={() => {
                setError(null);
                setModal(true);
              }}
            />
            {!!onDeclarePayment && (
              <Button
                title={t('advancePayment.action_declarePayment')}
                icon="checkmark-circle-outline"
                variant="secondary"
                onPress={onDeclarePayment}
              />
            )}
          </View>
        ) : !!onDeclarePayment && (
          <Button
            title={t('advancePayment.action_declarePayment')}
            icon="checkmark-circle-outline"
            variant="secondary"
            onPress={onDeclarePayment}
          />
        )
      )}

      {/* Ιστορικό αποδεκτών */}
      {items.filter((i) => i.status === 'accepted').length > 0 && (
        <View style={styles.history}>
          {items
            .filter((i) => i.status === 'accepted')
            .map((i) => (
              <View key={i.id} style={styles.histRow}>
                <Text style={styles.histAmount}>
                  ✓ {formatAmount(Number(i.amount), agreement.currency)}
                </Text>
                <Text style={styles.histDate}>{formatDateTime(i.created_at)}</Text>
              </View>
            ))}
        </View>
      )}

      {/* Modal καταγραφής */}
      <Modal visible={modal} transparent animationType="fade" onRequestClose={() => setModal(false)}>
        <KeyboardAvoidingView style={{ flex: 1 }} behavior={Platform.OS === 'ios' ? 'padding' : 'height'}>
        <View style={styles.overlay}>
          <View style={styles.modalCard}>
            <Text style={styles.modalTitle}>{t('advancePayment.modalTitle')}</Text>
            <Text style={styles.modalHint}>
              {t('advancePayment.modalHint', { amount: formatAmount(remaining, agreement.currency) })}
            </Text>
            <Text style={styles.disclaimer}>{t('advancePayment.recordOnlyDisclaimer')}</Text>
            <TextInput
              style={styles.input}
              value={amount}
              onChangeText={(v) => setAmount(formatGroupedAmount(v))}
              placeholder={t('advancePayment.placeholder_amount')}
              placeholderTextColor={colors.textMuted}
              keyboardType="decimal-pad"
            />
            <TextInput
              style={[styles.input, styles.multiline]}
              value={note}
              onChangeText={setNote}
              placeholder={t('advancePayment.placeholder_note')}
              placeholderTextColor={colors.textMuted}
              multiline
            />
            <ErrorText>{error}</ErrorText>
            <Button title={t('advancePayment.action_record')} loading={busy} onPress={submit} />
            <Pressable onPress={() => setModal(false)} hitSlop={8} style={styles.cancel}>
              <Text style={styles.cancelText}>{t('common.cancel')}</Text>
            </Pressable>
          </View>
        </View>
        </KeyboardAvoidingView>
      </Modal>

      {!!error && !modal && <ErrorText>{error}</ErrorText>}
    </View>
  );
}

type RowStyles = ReturnType<typeof makeStyles>;

function Row({
  label,
  value,
  strong,
  accent,
  styles,
}: {
  label: string;
  value: string;
  strong?: boolean;
  accent?: boolean;
  styles: RowStyles;
}) {
  return (
    <View style={styles.row}>
      <Text style={styles.rowLabel}>{label}</Text>
      <Text style={[styles.rowValue, strong && styles.rowStrong, accent && styles.rowAccent]}>
        {value}
      </Text>
    </View>
  );
}

function makeStyles(colors: AppColors) {
  return StyleSheet.create({
    card: {
      backgroundColor: colors.surface,
      borderRadius: radius.lg,
      borderWidth: 1,
      borderColor: colors.border,
      padding: spacing.md,
      gap: spacing.sm,
      ...shadow.card,
    },
    sectionTitle: {
      color: colors.textMuted,
      fontSize: fontSize.sm,
      fontWeight: '600',
    },
    row: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    rowLabel: {
      color: colors.textMuted,
      fontSize: fontSize.md,
    },
    rowValue: {
      color: colors.text,
      fontSize: fontSize.md,
      fontWeight: '600',
    },
    rowStrong: {
      fontSize: fontSize.lg,
      fontWeight: '800',
    },
    rowAccent: {
      color: colors.success,
    },
    paidNotice: {
      color: colors.success,
      fontSize: fontSize.sm,
      fontWeight: '700',
    },
    notice: {
      backgroundColor: colors.surfaceAlt,
      borderRadius: radius.md,
      padding: spacing.md,
      gap: spacing.sm,
      marginTop: spacing.xs,
    },
    noticeWaiting: {
      backgroundColor: 'rgba(251,191,36,0.10)',
      borderWidth: 1,
      borderColor: 'rgba(251,191,36,0.30)',
    },
    noticeText: {
      color: colors.text,
      fontSize: fontSize.sm,
      lineHeight: 20,
    },
    noteText: {
      color: colors.textMuted,
      fontSize: fontSize.sm,
      fontStyle: 'italic',
    },
    respondRow: {
      flexDirection: 'row',
      gap: spacing.sm,
    },
    respondBtn: { flex: 1 },
    btnRow: {
      flexDirection: 'column',
      gap: spacing.sm,
    },
    rejected: {
      color: colors.danger,
      fontSize: fontSize.sm,
      lineHeight: 19,
    },
    history: {
      gap: spacing.xs,
      borderTopWidth: 1,
      borderTopColor: colors.border,
      paddingTop: spacing.sm,
    },
    histRow: {
      flexDirection: 'row',
      justifyContent: 'space-between',
    },
    histAmount: {
      color: colors.success,
      fontSize: fontSize.sm,
      fontWeight: '700',
    },
    histDate: {
      color: colors.textMuted,
      fontSize: fontSize.sm,
    },
    overlay: {
      flex: 1,
      backgroundColor: 'rgba(0,0,0,0.5)',
      justifyContent: 'center',
      padding: spacing.lg,
    },
    modalCard: {
      backgroundColor: colors.surface,
      borderRadius: radius.lg,
      padding: spacing.lg,
      gap: spacing.md,
    },
    modalTitle: {
      color: colors.text,
      fontSize: fontSize.lg,
      fontWeight: '700',
    },
    modalHint: {
      color: colors.textMuted,
      fontSize: fontSize.sm,
    },
    disclaimer: {
      color: '#F59E0B',
      fontSize: fontSize.xs,
      lineHeight: 16,
    },
    input: {
      backgroundColor: colors.surfaceAlt,
      borderRadius: radius.md,
      borderWidth: 1,
      borderColor: colors.border,
      paddingHorizontal: spacing.md,
      height: 50,
      color: colors.text,
      fontSize: fontSize.md,
    },
    multiline: {
      height: 80,
      paddingTop: spacing.sm,
      textAlignVertical: 'top',
    },
    cancel: {
      alignSelf: 'center',
      paddingVertical: spacing.xs,
    },
    cancelText: {
      color: colors.textMuted,
      fontSize: fontSize.md,
      fontWeight: '600',
    },
    error: {
      color: colors.danger,
      fontSize: fontSize.sm,
    },
  });
}
